import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && err.config?.headers?.Authorization) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(err);
  }
);

const fmtDate = (d) => (d ? new Date(d).toLocaleString('ru-RU') : '—');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('ru-RU') : '—');
const parseTags = (s) => [...new Set(String(s || '').split(/[,;#]/).map(t => t.trim()).filter(Boolean))];

const UptimeBar = ({ snapshots, days = 90 }) => {
  const byDate = {};
  (snapshots || []).forEach((s) => { byDate[s.date] = s; });
  const cells = [];
  const today = new Date();
  let up = 0, known = 0;
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date(today); dt.setUTCDate(dt.getUTCDate() - i);
    const key = dt.toISOString().slice(0, 10);
    const s = byDate[key];
    let cls = 'unknown';
    if (s) { known++; if (s.online) { cls = 'up'; up++; } else cls = 'down'; }
    const tip = s ? `${key}${s.online ? ' · online' : ' · offline'}${s.responseTime ? ' · ' + s.responseTime + 'мс' : ''}${s.rknBlocked ? ' · РКН⛔' : ''}` : `${key} · нет данных`;
    cells.push(<span key={key} className={`uptime-cell ${cls}`} title={tip} />);
  }
  const pct = known ? Math.round((up / known) * 100) : null;
  return (
    <div className="uptime-block">
      <div className="uptime-head">
        <span className="uptime-title">Аптайм за {days} дней</span>
        <span className="uptime-pct">{pct != null ? `${pct}%` : '—'}</span>
      </div>
      <div className="uptime-bar">{cells}</div>
      <div className="uptime-legend">
        <span><i className="uptime-cell up" /> доступно</span>
        <span><i className="uptime-cell down" /> недоступно</span>
        <span><i className="uptime-cell unknown" /> нет данных</span>
      </div>
    </div>
  );
};

const StatusCard = ({ title, value, icon, color, subtext }) => (
  <div className={`status-card ${color || 'gray'}`}>
    <div className="card-icon">{icon}</div>
    <div className="card-content">
      <h3>{title}</h3>
      <p className="status-value">{value}</p>
      {subtext && <p className="status-subtext">{subtext}</p>}
    </div>
  </div>
);

const useCountUp = (target, ms = 700) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    const t = Number(target) || 0;
    if (t === 0) { setV(0); return; }
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(t * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
};

const Sparkline = ({ data }) => {
  const arr = Array.isArray(data) ? data : [];
  const max = Math.max(1, ...arr);
  const w = 100, h = 40, padTop = 5, padBottom = 5;
  const innerH = h - padTop - padBottom;
  const xOf = (i) => (arr.length > 1 ? (i * w) / (arr.length - 1) : w / 2);
  const yOf = (v) => padTop + innerH - (v / max) * innerH;
  const pts = arr.map((v, i) => [xOf(i), yOf(v)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(' ');
  const area = pts.length ? `${line} L${w} ${h} L0 ${h} Z` : '';
  const grid = [0, 0.5, 1].map((f) => (padTop + innerH * f).toFixed(2));
  const dayLabels = arr.map((_, i) => {
    const dt = new Date(); dt.setUTCDate(dt.getUTCDate() - (arr.length - 1 - i));
    return ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'][dt.getUTCDay()];
  });
  return (
    <div className="dash-spark-wrap">
      <svg className="dash-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(245,181,61,0.40)" />
            <stop offset="100%" stopColor="rgba(245,181,61,0)" />
          </linearGradient>
        </defs>
        {grid.map((gy, i) => <line key={i} className="dash-spark-grid" x1="0" y1={gy} x2={w} y2={gy} vectorEffect="non-scaling-stroke" />)}
        {area && <path d={area} fill="url(#sparkfill)" />}
        {line && <path className="dash-spark-line" d={line} fill="none" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="dash-spark-dots">
        {pts.map((p, i) => {
          const isLast = i === pts.length - 1;
          return <span key={i} className={`dash-spark-dot ${isLast ? 'last' : ''}`} style={{ left: `${(p[0] / w) * 100}%`, top: `${(p[1] / h) * 100}%` }} title={`${dayLabels[i]} · ${arr[i]} кликов`} />;
        })}
      </div>
      <div className="dash-spark-days">{dayLabels.map((d, i) => <span key={i} className={i === dayLabels.length - 1 ? 'now' : ''}>{d}</span>)}</div>
    </div>
  );
};

const MetricTile = ({ value, label, tone, delay }) => {
  const n = useCountUp(value);
  return (
    <div className={`dash-tile ${tone || ''}`} style={{ animationDelay: `${delay}ms` }}>
      <span className="dash-tile-num">{n}</span>
      <span className="dash-tile-label">{label}</span>
    </div>
  );
};

const DashboardSummary = ({ user, sites = [] }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/dashboard').then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const ok = d.problem === 0 && d.blocked === 0;
  const delta = d.trafficDelta;
  const trend = delta == null ? '—' : (delta >= 0 ? `+${delta}%` : `${delta}%`);
  const avgScore = sites.length ? Math.round(sites.reduce((a, s) => a + seoScore(s), 0) / sites.length) : 0;
  return (
    <section className="dash reveal">
      <div className="dash-hero">
        <div className="dash-hero-main">
          <p className="dash-eyebrow">Сводка · {user.role === 'admin' ? 'все сайты' : 'ваши сайты'}</p>
          <h2 className={`dash-headline ${ok ? 'ok' : 'warn'}`}>{ok ? 'Всё под контролем' : 'Требует внимания'}</h2>
          <div className="dash-hero-nums">
            <div className="dash-hero-num"><span className="big">{d.total}</span><span className="cap">сайтов в мониторинге</span></div>
            <div className="dash-hero-num accent"><span className="big">{d.problem}</span><span className="cap">с проблемами</span></div>
            <div className="dash-hero-num"><span className={`big seo-${scoreTone(avgScore)}`}>{avgScore}</span><span className="cap">SEO‑скор средний</span></div>
          </div>
        </div>
        <div className="dash-hero-spark">
          <div className="dash-spark-head">
            <span className="dash-spark-title">Поисковый трафик · 7 дн.</span>
            <span className={`dash-spark-trend ${delta == null ? '' : delta >= 0 ? 'up' : 'down'}`}>{d.trafficCurr} кликов · {trend}</span>
          </div>
          <Sparkline data={d.series} />
        </div>
      </div>
      <div className="dash-grid">
        <MetricTile value={d.online} label="Online" tone="good" delay={0} />
        <MetricTile value={d.offline} label="Offline" tone={d.offline ? 'bad' : ''} delay={60} />
        <MetricTile value={d.blocked} label="В реестре РКН" tone={d.blocked ? 'bad' : ''} delay={120} />
        <MetricTile value={d.sslSoon} label="SSL ≤ 14 дн." tone={d.sslSoon ? 'warn' : ''} delay={180} />
        <MetricTile value={d.domSoon} label="Домен ≤ 30 дн." tone={d.domSoon ? 'warn' : ''} delay={240} />
      </div>
    </section>
  );
};

const seoScore = (site) => {
  let s = 0;
  if (site.uptime_data?.online === true) s += 15;
  if (site.ssl_data?.valid === true) s += 15;
  const op = site.onpage_data;
  if (op?.available) { if (op.title) s += 8; if (op.h1) s += 7; if (op.canonical) s += 5; if (op.metaDesc) s += 5; }
  const hd = site.headers_data;
  if (hd?.available) s += Math.round((hd.score || 0) / 5 * 10);
  const wv = site.webvitals_data;
  if (wv && wv.available !== false) { if (wv.lcp?.rating === 'good') s += 5; if (wv.fid?.rating === 'good') s += 3; if (wv.cls?.rating === 'good') s += 2; }
  const idx = site.indexation_data;
  if (idx?.google?.indexed > 0) s += 10;
  if (idx?.yandex?.history?.crawlErrors === 0) s += 5;
  if (site.rkn_data?.blocked === false) s += 10;
  return Math.max(0, Math.min(100, s));
};
const scoreTone = (s) => (s >= 75 ? 'good' : s >= 45 ? 'mid' : 'bad');

const SeoScore = ({ value, size = 'sm' }) => (
  <div className={`seo-score ${size} ${scoreTone(value)}`} style={{ '--deg': Math.round(value / 100 * 360) + 'deg' }} title={`SEO-скор: ${value}/100`}>
    <span className="seo-score-num">{value}</span>
  </div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось войти');
    } finally { setLoading(false); }
  };

  return (
    <div className="login-shell">
      <div className="login-brand">
        <div className="brand-mark"><span className="live-dot" /> MONITOR</div>
        <h1 className="brand-title">Панель<br />наблюдения<br />за сайтами</h1>
        <p className="brand-sub">Безопасность · доступность · индексация · реестр РКН — в одном окне, для каждого участника команды отдельно.</p>
        <div className="brand-ticker" aria-hidden="true">
          <span>SSL</span><span>UPTIME</span><span>VIRUSTOTAL</span><span>ЯНДЕКС</span><span>GSC</span><span>РКН</span><span>WHOIS</span><span>CrUX</span>
        </div>
      </div>
      <form className="login-card" onSubmit={submit}>
        <p className="login-eyebrow">Вход в систему</p>
        <h2 className="login-head">Добро пожаловать</h2>
        <label className="field">
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@team.ru" autoComplete="email" required />
        </label>
        <label className="field">
          <span>Пароль</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required />
        </label>
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Входим…' : 'Войти →'}</button>
        <p className="login-foot">Доступ выдаёт администратор команды.</p>
      </form>
    </div>
  );
};

const SiteDetail = ({ site, onBack, onUpdate, user }) => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [idxLoading, setIdxLoading] = useState(false);
  const [indexModalOpen, setIndexModalOpen] = useState(false);
  const [indexUrls, setIndexUrls] = useState('');
  const [history, setHistory] = useState([]);
  const [editName, setEditName] = useState(site.name || '');
  const [editUrl, setEditUrl] = useState(site.url);
  const [editTags, setEditTags] = useState((Array.isArray(site.tags) ? site.tags : []).join(', '));
  const [editNotes, setEditNotes] = useState(site.notes || '');
  const [editChecks, setEditChecks] = useState((Array.isArray(site.page_checks) ? site.page_checks : []).map((c) => ({ path: c.path || '/', keyword: c.keyword || '' })));

  const handleRecheck = async () => {
    setLoading(true);
    try { await api.put(`/sites/${site.id}/check`); await onUpdate(); }
    catch { alert('Ошибка при перепроверке'); }
    finally { setLoading(false); }
  };

  const handleSaveEdit = async () => {
    try { await api.put(`/sites/${site.id}`, { name: editName, url: editUrl, tags: parseTags(editTags), notes: editNotes, page_checks: editChecks }); setEditing(false); await onUpdate(); }
    catch (e) { alert('Ошибка сохранения: ' + (e.response?.data?.error || e.message)); }
  };

  const handleIndexation = async () => {
    setIdxLoading(true);
    try { await api.put(`/sites/${site.id}/indexation`); await onUpdate(); }
    catch { alert('Ошибка проверки индексации'); }
    finally { setIdxLoading(false); }
  };

  // Отправка страниц в индекс: автоподтягивание URL из sitemap
  const openIndexModal = async () => {
    setIdxLoading(true);
    try {
      const { data } = await api.get(`/sites/${site.id}/sitemap-urls`);
      const list = Array.isArray(data.urls) && data.urls.length ? data.urls : [site.url + (site.url.endsWith('/') ? '' : '/')];
      setIndexUrls(list.join('\n'));
      setIndexModalOpen(true);
    } catch (e) {
      setIndexUrls(site.url + (site.url.endsWith('/') ? '' : '/') + '\n');
      setIndexModalOpen(true);
    } finally {
      setIdxLoading(false);
    }
  };

  const submitIndexation = async () => {
    setIdxLoading(true);
    try {
      const urls = indexUrls.split('\n').map(u => u.trim()).filter(Boolean);
      if (!urls.length) throw new Error('Нет URL для отправки');
      const { data } = await api.post(`/sites/${site.id}/submit-index`, { urls, engines: ['google', 'yandex'] });
      const results = data.results || [];
      const byEngine = {};
      results.forEach(r => { (byEngine[r.engine] = byEngine[r.engine] || []).push(r); });
      let msg = `URL: ${urls.length}\n`;
      Object.keys(byEngine).forEach(eng => {
        const list = byEngine[eng];
        const ok = list.filter(r => r.status === 'success').length;
        msg += `\n${eng === 'google' ? '🇬 Google' : '🔍 Яндекс'}: успешно ${ok} из ${list.length}`;
        const err = list.find(r => r.status === 'error');
        if (err) msg += `\nОшибка: ${err.message}`;
      });
      alert(msg);
      setIndexModalOpen(false);
      await onUpdate();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.error || err.message));
    } finally {
      setIdxLoading(false);
    }
  };

  const getColor = (c) => (c === true ? 'green' : c === false ? 'red' : 'gray');
  const getVitalColor = (r) => (r === 'good' ? 'green' : r === 'needs-improvement' ? 'yellow' : r === 'poor' ? 'red' : 'gray');
  useEffect(() => { api.get(`/sites/${site.id}/history?days=90`).then(({ data }) => setHistory(data)).catch(() => {}); }, [site.id]);

  const sec = { safeBrowsing: site.safe_browsing_data, virusTotal: site.virustotal_data, rep: site.apivoid_data, rkn: site.rkn_data };
  const tech = { ssl: site.ssl_data, uptime: site.uptime_data, whois: site.whois_data, webVitals: site.webvitals_data };
  const search = { bing: site.bing_data, yandex: site.yandex_data, gsc: site.gsc_data };
  const idx = site.indexation_data;
  const integ = { deface: site.deface_data, redirects: site.redirects_data, dns: site.dns_data, headers: site.headers_data, onpage: site.onpage_data, robots: site.robots_data };

  return (
    <div className="site-detail reveal">
      <div className="site-detail-header">
        <button onClick={onBack} className="back-btn">← Назад</button>
        {user.role !== 'viewer' && (
          <div className="header-actions">
            <button onClick={openIndexModal} disabled={idxLoading} className="ghost-btn">📤 В индекс</button>
            <button onClick={handleIndexation} disabled={idxLoading} className="ghost-btn">{idxLoading ? '⏳ Индексация…' : '🔍 Индексация'}</button>
            <button onClick={handleRecheck} disabled={loading} className="ghost-btn">{loading ? '⏳ Проверка…' : '🔄 Перепроверить'}</button>
          </div>
        )}
      </div>
      {user.role === 'viewer' && (
        <div className="viewer-banner" role="status">
          <span className="viewer-banner-eye" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
          </span>
          <span className="viewer-banner-text"><b>Режим просмотра</b> — вы видите данные этого сайта в реальном времени; изменения вносит администратор команды.</span>
        </div>
      )}
      <div className="site-info-header">
        {editing ? (
          <div className="edit-form">
            <div className="edit-field"><label>Название сайта</label><input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder={site.domain} /></div>
            <div className="edit-field"><label>URL сайта</label><input type="url" value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="https://example.com" /></div>
            <div className="edit-field"><label>Теги (через запятую)</label><input type="text" className="tags-input" value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="casino, mirror, client-A" /></div>
            <div className="edit-field"><label>Заметки (история инцидентов, комментарии)</label><textarea className="notes-input" rows={3} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Напр.: 12.05 падал апстрим, чинили nginx; владелец просил следить за SSL" /></div>
            <div className="edit-field">
              <label>Мониторинг страниц (статус 200 + контрольное слово)</label>
              <div className="checks-editor">
                {editChecks.map((c, i) => (
                  <div className="check-row" key={i}>
                    <input type="text" value={c.path} onChange={(e) => { const n = [...editChecks]; n[i] = { ...n[i], path: e.target.value }; setEditChecks(n); }} placeholder="/login или https://…" />
                    <input type="text" value={c.keyword} onChange={(e) => { const n = [...editChecks]; n[i] = { ...n[i], keyword: e.target.value }; setEditChecks(n); }} placeholder="слово (необяз.)" />
                    <button type="button" className="ghost-btn danger tiny" onClick={() => setEditChecks(editChecks.filter((_, k) => k !== i))}>✕</button>
                  </div>
                ))}
                <button type="button" className="ghost-btn tiny" onClick={() => setEditChecks([...editChecks, { path: '/', keyword: '' }])}>➕ Добавить страницу</button>
              </div>
            </div>
            <div className="edit-actions"><button onClick={handleSaveEdit} className="btn-primary">💾 Сохранить</button><button onClick={() => setEditing(false)} className="ghost-btn">Отмена</button></div>
          </div>
        ) : (
          <>
            <h1 className="site-title">{site.name || site.domain}</h1>
            {user.role !== 'viewer' && <button onClick={() => setEditing(true)} className="ghost-btn">✏️ Редактировать</button>}
          </>
        )}
        <p className="site-url">{site.url}</p>
        <p className="site-meta">Добавлен: {fmtDay(site.createdAt)} · Проверен: {fmtDate(site.last_checked)}</p>
        <div className="seo-score-line"><SeoScore value={seoScore(site)} size="lg" /><span className="seo-score-cap">SEO‑скор сайта · uptime 15 + SSL 15 + on‑page 25 + заголовки 10 + CWV 10 + индексация 15 + РКН 10</span></div>
        <div className="uptime-wrap"><UptimeBar snapshots={history} days={90} /></div>
        {site.notes && !editing && <div className="site-notes"><span className="site-notes-label">📝 Заметка</span><p>{site.notes}</p></div>}
      </div>

      <section className="section">
        <h2>🛡️ Безопасность</h2>
        <div className="cards-grid">
          <StatusCard title="Safe Browsing" value={sec.safeBrowsing?.safe ? 'Безопасен' : (sec.safeBrowsing?.error || 'Угроза!')} icon="🛡️" color={getColor(sec.safeBrowsing?.safe)} />
          <StatusCard title="VirusTotal" value={sec.virusTotal?.malicious ? 'Вредоносный!' : (sec.virusTotal?.error || 'Чисто')} icon="🦠" color={getColor(sec.virusTotal?.malicious === false)} />
          <StatusCard title="Репутация (abuse.ch)" value={sec.rep?.listed ? 'В блэклистах!' : (sec.rep?.available ? 'Чисто' : (sec.rep?.noKey ? 'Не настроено' : 'Ошибка ключа'))} icon="" color={sec.rep?.listed ? 'red' : (sec.rep?.available ? 'green' : 'gray')} subtext={sec.rep?.listed ? `${sec.rep.count} угроз${sec.rep.threats?.length ? ` · ${sec.rep.threats.join(', ')}` : ''}` : (sec.rep?.available ? 'проверено по abuse.ch URLhaus · бесплатно' : (sec.rep?.error || ''))} />
          <StatusCard title="РКН / Реестр" value={sec.rkn?.blocked === true ? 'В реестре!' : (sec.rkn?.blocked === false ? 'Не заблокирован' : '—')} icon="🇷🇺" color={sec.rkn?.blocked === false ? 'green' : (sec.rkn?.blocked === true ? 'red' : 'gray')} subtext={sec.rkn?.blocked ? `${sec.rkn.count} записей${sec.rkn.entries?.[0]?.reason ? ` · ${sec.rkn.entries[0].reason}` : ''}${sec.rkn.entries?.[0]?.date ? ` · ${sec.rkn.entries[0].date}` : ''}` : (sec.rkn?.blocked === false ? `не в реестре · ${sec.rkn.eaisChecked ? 'сверено с eais' : '⚠ eais недоступен — сверь по ссылке'}${sec.rkn.ip ? ` · IP ${sec.rkn.ip}` : ''}` : (sec.rkn?.error || ''))} />
          <StatusCard title="Доступность из РФ" value={sec.rkn?.availability?.checked ? (sec.rkn.availability.ruAvailable ? 'Доступен из РФ' : 'Недоступен из РФ') : '—'} icon="🌐" color={sec.rkn?.availability?.checked ? (sec.rkn.availability.ruAvailable ? 'green' : (sec.rkn.availability.likelyBlocked ? 'red' : 'yellow')) : 'gray'} subtext={sec.rkn?.availability?.checked ? `${sec.rkn.availability.likelyBlocked ? '⚠ вероятно блокировка · ' : ''}RU ${(sec.rkn.availability.ru || []).filter(r => r.available).length}/${(sec.rkn.availability.ru || []).length} · мир ${(sec.rkn.availability.foreign || []).filter(f => f.available).length}/${(sec.rkn.availability.foreign || []).length}` : (sec.rkn?.availability?.error || 'нажми «Перепроверить»')} />
        </div>
        <div className="section-note">
          <a href="https://eais.rkn.gov.ru/" target="_blank" rel="noopener noreferrer">🔗 Сверить в официальном сервисе РКН (eais.rkn.gov.ru) ↗</a>
          <span>— первоисточник, покрывает все основания (включая ФНС 15.1)</span>
        </div>
      </section>

      <section className="section">
        <h2>🧩 Целостность сайта</h2>
        <div className="cards-grid">
          <StatusCard title="Deface (хеш страницы)" value={integ.deface?.available ? (integ.deface.changed ? 'Изменилось!' : 'Без изменений') : '—'} icon="🛡️" color={integ.deface?.available ? (integ.deface.changed ? 'red' : 'green') : 'gray'} subtext={integ.deface?.available ? `хеш: ${integ.deface.hash?.slice(0, 12)}… · ${integ.deface.size} байт${integ.deface.title ? ' · ' + integ.deface.title : ''}` : (integ.deface?.error || 'нажми «Перепроверить»')} />
          <StatusCard title="Редиректы" value={integ.redirects?.available ? (integ.redirects.foreignRedirect ? 'На чужой домен!' : `${integ.redirects.hops} хопов`) : '—'} icon="↪️" color={integ.redirects?.available ? (integ.redirects.foreignRedirect ? 'red' : 'green') : 'gray'} subtext={integ.redirects?.available ? `итог: ${integ.redirects.finalHost}` : (integ.redirects?.error || 'нажми «Перепроверить»')} />
          <StatusCard title="DNS (A / NS)" value={integ.dns?.available ? `${integ.dns.a?.length || 0} A · ${integ.dns.ns?.length || 0} NS` : '—'} icon="🌐" color={integ.dns?.available ? (integ.dns.changed ? 'yellow' : 'green') : 'gray'} subtext={integ.dns?.available ? `NS: ${(integ.dns.ns || []).slice(0, 2).join(', ') || '—'}` : (integ.dns?.error || 'нажми «Перепроверить»')} />
        </div>
      </section>

      <section className="section">
        <h2>📋 Аудит сайта</h2>
        <div className="cards-grid">
          <StatusCard title="Заголовки безопасности" value={integ.headers?.available ? `${integ.headers.score}/5` : '—'} icon="🔐" color={integ.headers?.available ? (integ.headers.score >= 4 ? 'green' : integ.headers.score >= 2 ? 'yellow' : 'red') : 'gray'} subtext={integ.headers?.available ? `HSTS ${integ.headers.hsts ? '✓' : '✗'} · CSP ${integ.headers.csp ? '✓' : '✗'} · XFO ${integ.headers.xfo ? '✓' : '✗'} · XCTO ${integ.headers.xcto ? '✓' : '✗'} · RP ${integ.headers.referrer ? '✓' : '✗'}` : (integ.headers?.error || 'нажми «Перепроверить»')} />
          <StatusCard title="On-page (title / H1)" value={integ.onpage?.available ? (integ.onpage.title ? `${integ.onpage.titleLen} симв.` : 'Нет title') : '—'} icon="📝" color={integ.onpage?.available ? (integ.onpage.title && integ.onpage.h1 ? 'green' : 'yellow') : 'gray'} subtext={integ.onpage?.available ? `title: ${integ.onpage.title ? '✓' : '✗'} · H1: ${integ.onpage.h1 ? '✓' : '✗'} · canonical: ${integ.onpage.canonical ? '✓' : '✗'} · description: ${integ.onpage.metaDesc ? '✓' : '✗'}` : (integ.onpage?.error || 'нажми «Перепроверить»')} />
          <StatusCard title="robots.txt / sitemap" value={integ.robots?.available ? (integ.robots.robotsOk && integ.robots.sitemapOk ? 'OK' : 'Проблема') : '—'} icon="🤖" color={integ.robots?.available ? (integ.robots.robotsOk && integ.robots.sitemapOk ? 'green' : 'yellow') : 'gray'} subtext={integ.robots?.available ? `robots: ${integ.robots.robotsOk ? '✓' : '✗'} · sitemap: ${integ.robots.sitemapOk ? '✓ (' + (integ.robots.sitemapUrls || 0) + ' URL)' : '✗'}` : (integ.robots?.robotsError || integ.robots?.sitemapError || 'нажми «Перепроверить»')} />
          <StatusCard title="Structured data / OG" value={integ.onpage?.available ? (integ.onpage.jsonLd?.count ? `${integ.onpage.jsonLd.count} JSON-LD` : 'Нет JSON-LD') : '—'} icon="🧬" color={integ.onpage?.available ? (integ.onpage.jsonLd?.count && integ.onpage.og?.image ? 'green' : (integ.onpage.jsonLd?.count || integ.onpage.og?.title ? 'yellow' : 'red')) : 'gray'} subtext={integ.onpage?.available ? `типы: ${integ.onpage.jsonLd?.types?.slice(0, 3).join(', ') || '—'} · OG title ${integ.onpage.og?.title ? '✓' : '✗'} · OG image ${integ.onpage.og?.image ? '✓' : '✗'} · Twitter ${integ.onpage.twitter?.card ? '✓' : '✗'}` : (integ.onpage?.error || 'нажми «Перепроверить»')} />
        </div>
      </section>

      {(Array.isArray(site.page_checks) && site.page_checks.length > 0) && (
        <section className="section">
          <h2>🩺 Мониторинг страниц</h2>
          <div className="checks-list">
            {site.page_checks.map((c, i) => (
              <div className={`check-item ${c.lastOk ? 'ok' : (c.lastOk === false ? 'bad' : 'idle')}`} key={i}>
                <span className={`check-pulse ${c.lastOk ? 'ok' : (c.lastOk === false ? 'bad' : 'idle')}`} />
                <span className="check-path mono">{c.path}</span>
                {c.keyword && <span className="check-kw">«{c.keyword}» {c.lastFound ? '✓' : (c.lastFound === false ? '✗' : '')}</span>}
                <span className="check-status">HTTP {c.lastStatus ?? '—'}</span>
                <span className="check-time">{c.lastChecked ? fmtDate(c.lastChecked) : 'не проверялось'}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="section">
        <h2>⚙️ Технические данные</h2>
        <div className="cards-grid">
          <StatusCard title="SSL Сертификат" value={tech.ssl?.valid ? `${tech.ssl.daysRemaining} дн.` : 'Недействителен'} icon="🔒" color={getColor(tech.ssl?.valid)} subtext={tech.ssl?.valid ? tech.ssl?.issuer : (tech.ssl?.error || 'Проверьте сертификат')} />
          <StatusCard title="Uptime" value={tech.uptime?.online ? 'Online' : 'Offline'} icon="⚡" color={getColor(tech.uptime?.online)} subtext={`HTTP ${tech.uptime?.statusCode ?? '—'}`} />
          <StatusCard title="Время отклика" value={tech.uptime?.responseTime || 'N/A'} icon="⏱️" color="green" />
          <StatusCard title="WHOIS (домен)" value={tech.whois?.daysRemaining ? `${tech.whois.daysRemaining} дн.` : 'Нет данных'} icon="📅" color={tech.whois?.daysRemaining > 30 ? 'green' : (tech.whois?.daysRemaining ? 'yellow' : 'gray')} subtext={tech.whois?.registrar} />
        </div>
      </section>

      <section className="section">
        <h2>📊 Core Web Vitals</h2>
        {tech.webVitals?.available === false ? (
          <div className="no-data">{tech.webVitals?.error || 'Нет данных для этого сайта в Chrome UX Report'}</div>
        ) : (
          <div className="cards-grid">
            <StatusCard title="LCP (загрузка)" value={tech.webVitals?.lcp?.value ? `${Math.round(tech.webVitals.lcp.value)} мс` : 'N/A'} icon="⏳" color={getVitalColor(tech.webVitals?.lcp?.rating)} subtext={tech.webVitals?.lcp?.rating === 'good' ? 'Хорошо' : (tech.webVitals?.lcp?.rating === 'needs-improvement' ? 'Требует улучшения' : 'Плохо')} />
            <StatusCard title="FID (интерактивность)" value={tech.webVitals?.fid?.value ? `${Math.round(tech.webVitals.fid.value)} мс` : 'N/A'} icon="👆" color={getVitalColor(tech.webVitals?.fid?.rating)} subtext={tech.webVitals?.fid?.rating === 'good' ? 'Хорошо' : (tech.webVitals?.fid?.rating === 'needs-improvement' ? 'Требует улучшения' : 'Плохо')} />
            <StatusCard title="CLS (стабильность)" value={tech.webVitals?.cls?.value ? tech.webVitals.cls.value.toFixed(3) : 'N/A'} icon="📐" color={getVitalColor(tech.webVitals?.cls?.rating)} subtext={tech.webVitals?.cls?.rating === 'good' ? 'Хорошо' : (tech.webVitals?.cls?.rating === 'needs-improvement' ? 'Требует улучшения' : 'Плохо')} />
          </div>
        )}
      </section>

      <section className="section">
        <h2>📈 Поисковые данные</h2>
        <div className="cards-grid">
          <StatusCard title="Bing — клики" value={search.bing?.available ? search.bing.clicks : '—'} icon="🅱️" color={search.bing?.available ? 'green' : 'gray'} subtext={search.bing?.available ? `Показы: ${search.bing.impressions} · CTR: ${search.bing.ctr}%` : (search.bing?.error || 'Нет ключа')} />
          <StatusCard title="Яндекс — клики" value={search.yandex?.available ? `${search.yandex.source === 'popular' ? '≈ ' : ''}${search.yandex.clicks}` : '—'} icon="🔍" color={search.yandex?.available ? 'green' : 'gray'} subtext={search.yandex?.available ? (search.yandex.clicks > 0 ? `Показы: ${search.yandex.impressions} · CTR: ${search.yandex.ctr}%` : 'подключён · запросов за 30 дн. пока нет') : (search.yandex?.error || 'Нет токена')} />
          <StatusCard title="Google — клики" value={search.gsc?.available ? search.gsc.clicks : '—'} icon="🇬" color={search.gsc?.available ? 'green' : 'gray'} subtext={search.gsc?.available ? `Показы: ${search.gsc.impressions} · CTR: ${search.gsc.ctr}% · Поз: ${search.gsc.avgPosition}` : (search.gsc?.error || 'Нет доступа')} />
        </div>
        {[
          { key: 'yandex', label: '🔍 Топ запросов Яндекс', data: search.yandex?.topQueries },
          { key: 'bing', label: '🅱️ Топ запросов Bing', data: search.bing?.topQueries },
          { key: 'gsc', label: '🇬 Топ запросов Google', data: search.gsc?.topQueries }
        ].filter(s => s.data && s.data.length > 0).map(s => (
          <div className="queries-block" key={s.key}>
            <h4>{s.label}</h4>
            {s.data.map((q, i) => (
              <div className="query-row" key={i}><span className="q">{q.query}</span><span className="m">клики {q.clicks} · показы {q.impressions}</span></div>
            ))}
          </div>
        ))}
      </section>

      {site.positions_data && Array.isArray(site.positions_data.queries) && site.positions_data.queries.length > 0 && (
        <section className="section">
          <h2>🎯 Позиции по запросам {site.positions_data.date ? <span className="positions-date">· срез {fmtDay(site.positions_data.date)}</span> : null}</h2>
          <div className="positions-list">
            {site.positions_data.queries.map((q, i) => {
              const prev = (site.positions_data.prev || []).find(p => p.query === q.query);
              const delta = prev ? Math.round((q.position - prev.position) * 10) / 10 : null;
              const cls = delta == null ? 'flat' : (delta < 0 ? 'up' : delta > 0 ? 'down' : 'flat');
              return (
                <div className="position-row" key={i}>
                  <span className="position-query">{q.query}</span>
                  <span className="position-delta-wrap">
                    {delta != null && delta !== 0 && <span className={`position-delta ${cls}`}>{delta < 0 ? '▲' : '▼'} {Math.abs(delta)}</span>}
                    {(delta == null || delta === 0) && <span className="position-delta flat">{delta == null ? '·' : '— 0'}</span>}
                  </span>
                  <span className="position-pos">{q.position}</span>
                  <span className="position-clicks">{q.clicks} кл.</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="section">
        <h2>🗂️ Индексация</h2>
        {!idx ? (
          <div className="no-data">Нажми «🔍 Индексация» сверху, чтобы проверить страницы (Google: сводка по sitemap + выборка; Яндекс: обход по статусам + события; Bing: статистика обхода)</div>
        ) : (
          <>
            <div className="cards-grid">
              <StatusCard title="Google — в индексе" value={idx.google?.indexed ?? '—'} icon="🇬" color={idx.google?.indexed > 0 ? 'green' : 'gray'} subtext={idx.google && !idx.google.error ? `по показам за 90 дн. · отправлено ${idx.google.submitted} · карт сайта ${idx.google.sitemapsCount}` : (idx.google?.error || '')} />
              <StatusCard title="Google — выборка страниц" value={idx.google?.sampleChecked ? `${idx.google.sampleIndexed}/${idx.google.sampleChecked}` : '—'} icon="🔎" color={idx.google?.sampleChecked > 0 && idx.google?.sampleNotIndexed?.length === 0 ? 'green' : 'yellow'} subtext={idx.google?.sampleChecked ? `проиндекс./проверено · ${idx.google.sampleSource === 'sitemap' ? 'sitemap' : 'проиндексированные'}` : 'нет страниц для проверки'} />
              <StatusCard title="Яндекс — обход (2xx/3xx)" value={idx.yandex?.history?.crawlOk ?? '—'} icon="🔍" color={idx.yandex?.history?.crawlOk > 0 ? 'green' : 'gray'} subtext={idx.yandex?.history ? 'страниц за 30 дн. со статусом 2xx/3xx' : (idx.yandex?.error || '')} />
              <StatusCard title="Яндекс — ошибки обхода" value={idx.yandex?.history?.crawlErrors ?? '—'} icon="⚠️" color={idx.yandex?.history?.crawlErrors === 0 ? 'green' : (idx.yandex?.history?.crawlErrors > 0 ? 'yellow' : 'gray')} subtext={idx.yandex?.history?.breakdown ? (['HTTP_4XX', 'HTTP_5XX'].filter(k => idx.yandex.history.breakdown[k]).map(k => `${k}: ${idx.yandex.history.breakdown[k]}`).join(' · ') || 'нет 4xx/5xx') : 'страниц с 4xx/5xx за 30 дн.'} />
              <StatusCard title="Яндекс — исключено" value={idx.yandex?.recentExcluded?.length ?? '—'} icon="🚫" color={idx.yandex?.recentExcluded?.length === 0 ? 'green' : (idx.yandex?.recentExcluded?.length > 0 ? 'yellow' : 'gray')} subtext="последние события исключения" />
              <StatusCard title="Отправлено, без показов" value={idx.google?.notIndexed ?? '—'} icon="📤" color={idx.google?.notIndexed === 0 ? 'green' : (idx.google?.notIndexed > 0 ? 'yellow' : 'gray')} subtext="страниц в sitemap без показов за 90 дн." />
              <StatusCard title="Bing — обход" value={idx.bing?.crawlErrors != null ? `${idx.bing.crawlErrors} ошибок` : '—'} icon="🅱️" color={idx.bing?.crawlErrors === 0 ? 'green' : (idx.bing?.crawlErrors > 0 ? 'red' : 'gray')} subtext={idx.bing?.crawlOk != null ? `успешных ответов: ${idx.bing.crawlOk}` : (idx.bing?.error || 'API недоступен')} />
            </div>
            {idx.google?.sampleNotIndexed?.length > 0 && (
              <div className="queries-block"><h4>⚠️ Google: статус проверенных страниц (не PASS)</h4>
                {idx.google.sampleNotIndexed.map((p, i) => (<div className="query-row" key={i}><span className="q">{p.url}</span><span className="m">{p.coverage}{p.crawl ? ` · ${p.crawl}` : ''}{p.robots ? ` · robots: ${p.robots}` : ''}</span></div>))}
              </div>
            )}
            {idx.yandex?.recentExcluded?.length > 0 && (
              <div className="queries-block"><h4>🚫 Яндекс: последние исключённые страницы</h4>
                {idx.yandex.recentExcluded.map((p, i) => (<div className="query-row" key={i}><span className="q">{p.url}</span><span className="m">{p.reason}{p.httpStatus ? ` · HTTP ${p.httpStatus}` : ''} · {p.date}</span></div>))}
              </div>
            )}
            {idx.yandex?.recentAppeared?.length > 0 && (
              <div className="queries-block"><h4>✅ Яндекс: последние появившиеся в поиске</h4>
                {idx.yandex.recentAppeared.map((p, i) => (<div className="query-row" key={i}><span className="q">{p.url}</span><span className="m">{p.date}</span></div>))}
              </div>
            )}
            {idx.checkedAt && <p className="site-meta" style={{ marginTop: '12px' }}>Индексация проверена: {fmtDate(idx.checkedAt)}</p>}
          </>
        )}
      </section>

      {indexModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setIndexModalOpen(false)}>
          <div style={{ background: '#12161f', border: '1px solid #2a3040', borderRadius: 12, padding: 24, maxWidth: 600, width: '92%' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 10px 0' }}>📤 Отправить страницы на индексацию</h3>
            <p style={{ margin: '0 0 15px 0', opacity: 0.7, fontSize: '0.9em' }}>
              URL подтянуты из sitemap автоматически. Можно отредактировать список и отправить в Google Indexing API и Яндекс.Вебмастер.
            </p>
            <textarea
              value={indexUrls}
              onChange={(e) => setIndexUrls(e.target.value)}
              placeholder={`${site.url}/page-1\n${site.url}/page-2`}
              rows={10}
              style={{ width: '100%', marginBottom: 15, fontFamily: 'monospace', padding: 10, borderRadius: 6, border: '1px solid #2a3040', background: '#0b0e14', color: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setIndexModalOpen(false)} className="ghost-btn" disabled={idxLoading}>Отмена</button>
              <button onClick={submitIndexation} className="btn-primary" disabled={idxLoading}>
                {idxLoading ? '⏳ Отправка...' : '🚀 Отправить в индекс'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SitesView = ({ user }) => {
  const [sites, setSites] = useState([]); const [selected, setSelected] = useState(null);
  const [newUrl, setNewUrl] = useState(''); const [newOwner, setNewOwner] = useState(''); const [assignOther, setAssignOther] = useState(false);
  const [owners, setOwners] = useState(null); const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState(''); const [statusF, setStatusF] = useState('all'); const [tagF, setTagF] = useState('');
  const [importOpen, setImportOpen] = useState(false); const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false); const [importResult, setImportResult] = useState(null);
  const [newTags, setNewTags] = useState('');
  const [loading, setLoading] = useState(false); const [error, setError] = useState('');
  const [batchChecking, setBatchChecking] = useState(false);

  const load = useCallback(async () => { try { const { data } = await api.get('/sites'); setSites(data); } catch { setError('Ошибка загрузки сайтов'); } }, []);
  useEffect(() => { load(); const t = setInterval(load, 300000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (user.role === 'admin') api.get('/auth/admin/users').then(({ data }) => setOwners(data)).catch(() => {}); }, [user.role]);

  const openSite = (s) => { setSelected(s); localStorage.setItem('lastSiteId', String(s.id)); };
  const closeSite = () => { setSelected(null); localStorage.removeItem('lastSiteId'); };

  useEffect(() => {
    const sid = localStorage.getItem('lastSiteId');
    if (sid) api.get(`/sites/${sid}`).then(({ data }) => setSelected(data)).catch(() => localStorage.removeItem('lastSiteId'));
  }, []);
  const refreshSelected = async () => { const { data } = await api.get(`/sites/${selected.id}`); setSelected(data); load(); };

  const ownerOf = (s) => {
    if (!s.userId) return { kind: 'none', label: 'без владельца' };
    if (s.userId === user.id) return { kind: 'mine', label: 'мои' };
    const o = owners?.find(u => u.id === s.userId);
    return { kind: 'other', label: o ? (o.name || o.email) : `user #${s.userId}` };
  };

  const allTags = [...new Set(sites.flatMap((s) => Array.isArray(s.tags) ? s.tags : []))].sort((a, b) => a.localeCompare(b, 'ru'));

  const visible = sites.filter((s) => {
    if (user.role === 'admin') {
      if (filter === 'mine' && s.userId !== user.id) return false;
      if (filter === 'none' && s.userId) return false;
      if (filter !== 'all' && filter !== 'mine' && filter !== 'none' && s.userId !== Number(filter)) return false;
    }
    if (search) { const q = search.toLowerCase(); if (!(`${s.domain} ${s.name || ''}`.toLowerCase().includes(q))) return false; }
    if (statusF === 'online' && !s.uptime_data?.online) return false;
    if (statusF === 'offline' && s.uptime_data?.online !== false) return false;
    if (statusF === 'problem' && !(s.uptime_data?.online === false || s.ssl_data?.valid === false || s.rkn_data?.blocked === true)) return false;
    if (tagF && !(Array.isArray(s.tags) ? s.tags : []).includes(tagF)) return false;
    return true;
  });

  const addSite = async (e) => {
    e.preventDefault(); if (!newUrl) return; setLoading(true); setError('');
    try { await api.post('/sites', { url: newUrl, tags: parseTags(newTags), userId: user.role === 'admin' && newOwner ? Number(newOwner) : undefined }); setNewUrl(''); setNewOwner(''); setNewTags(''); await load(); }
    catch (err) { setError(err.response?.data?.error || 'Ошибка добавления сайта'); } finally { setLoading(false); }
  };

  const runImport = async () => {
    const items = importText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
      const [u, t] = line.split('|');
      return { url: (u || '').trim(), tags: (t || '').split(',').map((x) => x.trim()).filter(Boolean) };
    }).filter((x) => x.url);
    if (!items.length) { setImportResult({ added: 0, skipped: 0, errors: ['список пуст'] }); return; }
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post('/sites/import', { items, userId: user.role === 'admin' && newOwner ? Number(newOwner) : undefined });
      await load();
      if (data.added > 0) { setImportOpen(false); setImportText(''); setImportResult(null); }
      else setImportResult(data);
    } catch (e) { setImportResult({ added: 0, skipped: 0, errors: [e.response?.data?.error || e.message] }); }
    finally { setImporting(false); }
  };

  const batchCheck = async () => {
    if (!visible.length || batchChecking) return;
    if (!window.confirm(`Перепроверить ${visible.length} сайтов? Это может занять время.`)) return;
    setBatchChecking(true);
    try {
      await api.post('/sites/batch-check', { ids: visible.map(s => s.id) });
      await load();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.error || err.message));
    } finally {
      setBatchChecking(false);
    }
  };

  const exportCSV = () => {
    const rows = visible.map((s) => {
      const ow = ownerOf(s);
      return [s.domain, s.name || '', s.url, s.uptime_data?.online ? 'online' : 'offline', s.ssl_data?.valid ? 'valid' : 'invalid', s.ssl_data?.daysRemaining ?? '', s.rkn_data?.blocked === true ? 'blocked' : 'ok', (s.tags || []).join(';'), user.role === 'admin' ? ow.label : ''];
    });
    const head = ['domain', 'name', 'url', 'status', 'ssl', 'ssl_days', 'rkn', 'tags', 'owner'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `monitor-sites-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const deleteSite = async (id) => { if (!window.confirm('Удалить этот сайт?')) return; try { await api.delete(`/sites/${id}`); await load(); if (selected?.id === id) closeSite(); } catch { alert('Ошибка удаления'); } };

  if (selected) return <SiteDetail site={selected} owners={owners} user={user} onBack={closeSite} onUpdate={refreshSelected} />;

  return (
    <div className="reveal">
      <div className="view-head">
        <div><p className="view-eyebrow">Мониторинг</p><h1 className="view-title">Все сайты <span className="count-pill">{sites.length}</span></h1></div>
        {user.role !== 'viewer' && (
          <form className="add-site-form" onSubmit={addSite}>
            <input type="text" placeholder="https://example.com" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} />
            <input type="text" className="tags-input" placeholder="теги: casino, mirror" value={newTags} onChange={(e) => setNewTags(e.target.value)} />
            {user.role === 'admin' && owners && (
              <>
                <label className="assign-toggle" title="Создать сайт сразу для другого пользователя (по умолчанию — вам)">
                  <input type="checkbox" checked={assignOther} onChange={(e) => { setAssignOther(e.target.checked); if (!e.target.checked) setNewOwner(''); }} />
                  <span>👤 другому</span>
                </label>
                {assignOther && (
                  <select className="owner-select compact" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} title="Владелец нового сайта" required>
                    <option value="" disabled>выберите пользователя…</option>
                    {owners.filter((u) => u.id !== user.id).map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                  </select>
                )}
              </>
            )}
            <button type="submit" disabled={loading} className="btn-primary">{loading ? '⏳ Добавляем…' : '➕ Добавить'}</button>
          </form>
        )}
      </div>
      <DashboardSummary user={user} sites={sites} />
      <div className="sites-toolbar">
        <div className="toolbar-row">
          <input className="search-input" type="search" placeholder="🔍 поиск по домену / названию" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="owner-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} title="Статус">
            <option value="all">Любой статус</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="problem">С проблемами</option>
          </select>
          <select className="owner-filter" value={tagF} onChange={(e) => setTagF(e.target.value)} title="Тег">
            <option value="">Все теги</option>
            {allTags.map((t) => <option key={t} value={t}>#{t}</option>)}
          </select>
          {user.role === 'admin' && owners && (
            <select className="owner-filter" value={filter} onChange={(e) => setFilter(e.target.value)} title="Владелец">
              <option value="all">Все владельцы</option>
              <option value="mine">Только мои</option>
              <option value="none">Без владельца</option>
              {owners.filter((o) => o.id !== user.id).map((o) => <option key={o.id} value={o.id}>{o.email} ({o.sitesCount})</option>)}
            </select>
          )}
          {user.role !== 'viewer' && <button type="button" className="ghost-btn tiny" onClick={batchCheck} disabled={batchChecking || !visible.length} style={{marginRight: 8}}>{batchChecking ? '⏳ Проверка...' : '⚡ Проверить всё'}</button>}
          <button type="button" className="ghost-btn tiny" onClick={exportCSV} title="Выгрузить видимые сайты в CSV">⬇️ Экспорт CSV</button>
          {user.role !== 'viewer' && <button type="button" className="ghost-btn tiny" onClick={() => { setImportOpen(!importOpen); setImportResult(null); }}>📥 Импорт списком</button>}
        </div>
        {importOpen && (
          <div className="import-block">
            <p className="import-hint">Один домен на строку. Теги (необяз.) — после <span className="mono">|</span> через запятую. Пример: <span className="mono">example.com | casino, mirror</span></p>
            <textarea rows={6} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={"example.com | casino\nexample2.ru\nexample3.online | mirror"} />
            <div className="import-actions">
              <button type="button" className="btn-primary" disabled={importing} onClick={runImport}>{importing ? '⏳ Импорт…' : '📥 Добавить сайты'}</button>
              {user.role === 'admin' && owners && (
                <select className="owner-filter" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} title="Владелец импортируемых сайтов">
                  <option value="">владелец: я</option>
                  {owners.filter((o) => o.id !== user.id).map((o) => <option key={o.id} value={o.id}>{o.email}</option>)}
                </select>
              )}
            </div>
            {importResult && (
              <div className={`import-result ${importResult.added ? 'ok' : 'warn'}`}>
                ✅ Добавлено: <b>{importResult.added}</b> · пропущено (уже есть): <b>{importResult.skipped}</b>
                {importResult.errors?.length > 0 && <span className="import-errs"> · ошибок: {importResult.errors.join('; ')}</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
      {visible.length === 0 ? <div className="empty-state"><p>{sites.length === 0 ? '🌐 Добавьте первый сайт для мониторинга' : '🔍 Под этот фильтр сайтов нет'}</p></div> : (
        <div className="sites-grid">
          {visible.map((site, i) => { const ow = ownerOf(site); return (
            <div key={site.id} className="site-card" style={{ animationDelay: `${i * 60}ms` }} onClick={() => openSite(site)}>
              <div className="site-card-header"><h3>{site.name || site.domain}</h3><SeoScore value={seoScore(site)} size="sm" />{user.role !== 'viewer' && <button onClick={(e) => { e.stopPropagation(); deleteSite(site.id); }} className="delete-btn" title="Удалить">🗑️</button>}</div>
              <p className="site-url-small">{site.url}</p>
              {user.role === 'admin' && <span className={`owner-badge ${ow.kind}`}>👤 {ow.label}</span>}
              {Array.isArray(site.tags) && site.tags.length > 0 && <div className="tags-row">{site.tags.map((t) => <span key={t} className="tag-badge" onClick={(e) => { e.stopPropagation(); setTagF(t); }}>#{t}</span>)}</div>}
              <div className="site-card-status">
                <span className={`badge ${site.ssl_data?.valid ? 'green' : 'red'}`}>SSL {site.ssl_data?.valid ? '✓' : '✗'}</span>
                <span className={`badge ${site.uptime_data?.online ? 'green' : 'red'}`}>{site.uptime_data?.online ? 'Online' : 'Offline'}</span>
                {site.rkn_data?.blocked === true && <span className="badge red">РКН ⛔</span>}
              </div>
              <p className="site-card-date">Проверен: {fmtDate(site.last_checked)}</p>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
};

const KEY_FIELDS = [
  { key: 'yandexToken', label: 'Яндекс.Вебмастер', hint: 'Токен OAuth из настроек Вебмастера', ph: 'AgAAAAA…' },
  { key: 'bingKey', label: 'Bing Webmaster', hint: 'API Key из Bing Webmaster Tools', ph: '…' },
  { key: 'virusTotalKey', label: 'VirusTotal', hint: 'API key из профиля VT', ph: '…' },
  { key: 'abuseChKey', label: 'abuse.ch (репутация)', hint: 'Бесплатный Auth-Key с auth.abuse.ch (передаётся заголовком Auth-Key)', ph: '…' },
  { key: 'safeBrowsingKey', label: 'Google Safe Browsing', hint: 'API key Google Cloud', ph: '…' },
  { key: 'googleApiKey', label: 'Google (CrUX / Web Vitals)', hint: 'Тот же проект, включён Chrome UX Report API', ph: '…' },
  { key: 'telegramBotToken', label: 'Telegram-бот (личный)', hint: 'Необязательно: свой бот для алертов. Иначе — общий', ph: '123456:ABC-…' },
  { key: 'telegramChatId', label: 'Telegram chat ID', hint: 'Куда слать алерты по вашим сайтам', ph: '123456789' }
];
const gscEmail = (s) => { try { return JSON.parse(s)?.client_email || null; } catch { return null; } };
const ApiKeysView = ({ user }) => {
  const [keys, setKeys] = useState({}); const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(''); const [show, setShow] = useState({}); const [drag, setDrag] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => { (async () => { try { const { data } = await api.get('/settings'); setKeys(data.apiKeys || {}); setDraft(data.apiKeys || {}); } catch { setMsg('Не удалось загрузить настройки'); } finally { setLoading(false); } })(); }, []);

  const save = async () => { setSaving(true); setMsg(''); try { await api.put('/settings', draft); setKeys({ ...draft }); setMsg('✅ Ключи сохранены'); } catch (e) { setMsg('❌ ' + (e.response?.data?.error || e.message)); } finally { setSaving(false); } };
  const readGscFile = (file) => { if (!file) return; const r = new FileReader(); r.onload = () => setDraft({ ...draft, gscCredentials: String(r.result) }); r.readAsText(file); };

  const stateOf = (k) => { const own = !!(keys[k] && String(keys[k]).trim()); if (own) return 'own'; return isAdmin ? 'shared' : 'none'; };
  const stateLabel = { own: 'свой', shared: 'общий', none: 'не задан' };

  if (loading) return <div className="empty-state"><p>Загрузка настроек…</p></div>;
  const connectedEmail = gscEmail(draft.gscCredentials);
  const gscState = connectedEmail ? 'own' : (isAdmin ? 'shared' : 'none');

  return (
    <div className="reveal keys-view">
      <div className="view-head"><div><p className="view-eyebrow">Профиль</p><h1 className="view-title">🔑 API-ключи</h1>
        <p className="view-lead">{isAdmin
          ? 'Ваши ключи используются при проверке ваших сайтов. Пустое поле = общий ключ сервера (админский пул).'
          : 'Ваши ключи используются при проверке ваших сайтов. Общие ключи сервера вам недоступны: без своего ключа проверка этого источника не выполняется.'}</p>
      </div></div>

      <div className="gsc-block">
        <div className="gsc-head"><span className="key-label">Google Search Console <span className={`key-state ${gscState}`}>{stateLabel[gscState] === 'свой' ? 'личный аккаунт' : stateLabel[gscState]}</span></span></div>
        {connectedEmail ? (
          <div className="gsc-connected">
            <div className="gsc-avatar">G</div>
            <div className="gsc-meta"><span className="gsc-email">{connectedEmail}</span><span className="gsc-hint">Сервис-аккаунт подключён. Добавьте его в свой GSC как владельца/пользователя сайта.</span></div>
            <button type="button" className="ghost-btn danger" onClick={() => setDraft({ ...draft, gscCredentials: '' })}>Отключить</button>
          </div>
        ) : (
          <label className={`file-drop ${drag ? 'drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); readGscFile(e.dataTransfer.files?.[0]); }}>
            <input className="file-drop-input" type="file" accept=".json,application/json" onChange={(e) => readGscFile(e.target.files?.[0])} />
            <span className="file-drop-medal" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4" />
                <polyline points="6 10 12 4 18 10" />
                <path d="M4 20h16" />
              </svg>
            </span>
            <span className="file-drop-text"><b>Перетащите JSON сервис-аккаунта</b><span className="file-drop-or">или нажмите, чтобы выбрать файл</span></span>
            <span className="file-drop-hint">Файл вида <span className="mono">project-…-credentials.json</span> · хранится только в вашем профиле</span>
          </label>
        )}
        <textarea className="gsc-paste" placeholder="…или вставьте содержимое JSON сервис-аккаунта сюда" value={draft.gscCredentials && !connectedEmail ? draft.gscCredentials : ''} onChange={(e) => setDraft({ ...draft, gscCredentials: e.target.value })} rows={3} />
        {!connectedEmail && <p className="gsc-status-note">{isAdmin ? 'Без личного аккаунта поисковые данные Google берутся из общего сервис-аккаунта сервера.' : 'Без личного аккаунта поисковые данные Google по вашим сайтам недоступны.'}</p>}
      </div>

      <div className="keys-grid">
        {KEY_FIELDS.map((f) => { const st = stateOf(f.key); return (
          <label className="key-field" key={f.key}>
            <span className="key-label">{f.label} <span className={`key-state ${st}`}>{stateLabel[st]}</span></span>
            <div className="key-input-row">
              <input type={show[f.key] ? 'text' : 'password'} value={draft[f.key] || ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} placeholder={f.ph} autoComplete="off" />
              <button type="button" className="eye-btn" onClick={() => setShow({ ...show, [f.key]: !show[f.key] })} tabIndex={-1}>{show[f.key] ? '🙈' : '👁️'}</button>
            </div>
            <span className="key-hint">{f.hint}{st === 'none' ? ' · без ключа проверка не выполняется' : (st === 'shared' ? ' · используется общий ключ сервера' : '')}</span>
          </label>
        ); })}
      </div>
      <div className="thresholds-block">
        <div className="thresholds-head">
          <span className="key-label">🔔 Пороги алертов <span className="key-state own">ваши</span></span>
          <span className="key-hint">Пусто = значение по умолчанию. Применяются к вашим сайтам при каждой проверке.</span>
        </div>
        <div className="thresholds-grid">
          <label className="threshold-field"><span>SSL истекает, дней ≤</span><input type="number" min="1" max="365" value={draft.alertSettings?.sslDays ?? ''} onChange={(e) => setDraft({ ...draft, alertSettings: { ...(draft.alertSettings || {}), sslDays: e.target.value === '' ? null : Number(e.target.value) } })} placeholder="14" /></label>
          <label className="threshold-field"><span>Домен истекает, дней ≤</span><input type="number" min="1" max="3650" value={draft.alertSettings?.domainDays ?? ''} onChange={(e) => setDraft({ ...draft, alertSettings: { ...(draft.alertSettings || {}), domainDays: e.target.value === '' ? null : Number(e.target.value) } })} placeholder="30" /></label>
          <label className="threshold-field"><span>Падение трафика, % ≥</span><input type="number" min="1" max="99" value={draft.alertSettings?.trafficDropPct ?? ''} onChange={(e) => setDraft({ ...draft, alertSettings: { ...(draft.alertSettings || {}), trafficDropPct: e.target.value === '' ? null : Number(e.target.value) } })} placeholder="50" /></label>
        </div>
      </div>
      <div className="keys-actions"><button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Сохраняем…' : '💾 Сохранить ключи'}</button>{msg && <span className={`save-msg ${msg.startsWith('✅') ? 'ok' : 'err'}`}>{msg}</span>}</div>
    </div>
  );
};

const UsersView = () => {
  const [users, setUsers] = useState([]); const [allSites, setAllSites] = useState([]);
  const [open, setOpen] = useState({}); const [loading, setLoading] = useState(true); const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', telegramChatId: '' }); const [edit, setEdit] = useState({}); const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.get('/auth/admin/users'), api.get('/sites')]);
      setUsers(u.data); setAllSites(s.data);
    } catch { setMsg('Ошибка загрузки'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => { e.preventDefault(); setMsg(''); try { await api.post('/auth/admin/users', form); setForm({ email: '', password: '', name: '', role: 'user', telegramChatId: '' }); setMsg('✅ Пользователь создан'); await load(); } catch (err) { setMsg('❌ ' + (err.response?.data?.error || err.message)); } };
  const startEdit = (u) => { setEditingId(u.id); setEdit({ name: u.name || '', role: u.role, telegramChatId: u.telegramChatId || '', password: '' }); };
  const saveEdit = async (id) => { setMsg(''); try { const p = { name: edit.name, role: edit.role, telegramChatId: edit.telegramChatId }; if (edit.password) p.password = edit.password; await api.put(`/auth/admin/users/${id}`, p); setEditingId(null); setMsg('✅ Сохранено'); await load(); } catch (err) { setMsg('❌ ' + (err.response?.data?.error || err.message)); } };
  const remove = async (id) => { if (!window.confirm('Удалить пользователя? Его сайты перейдут вам.')) return; try { await api.delete(`/auth/admin/users/${id}`); await load(); } catch (err) { setMsg('❌ ' + (err.response?.data?.error || err.message)); } };

  if (loading) return <div className="empty-state"><p>Загрузка…</p></div>;

  return (
    <div className="reveal users-view">
      <div className="view-head"><div><p className="view-eyebrow">Администрирование</p><h1 className="view-title">👥 Пользователи</h1></div></div>
      <form className="create-user" onSubmit={create}><h3>Создать пользователя</h3>
        <div className="create-grid">
          <input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input type="text" placeholder="Пароль * (мин. 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <input type="text" placeholder="Имя" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}><option value="user">user</option><option value="admin">admin</option><option value="viewer">viewer</option></select>
          <input type="text" placeholder="Telegram chat ID" value={form.telegramChatId} onChange={(e) => setForm({ ...form, telegramChatId: e.target.value })} />
          <button type="submit" className="btn-primary">➕ Создать</button>
        </div>
      </form>
      {msg && <div className={`save-msg inline ${msg.startsWith('✅') ? 'ok' : 'err'}`}>{msg}</div>}
      <div className="users-list">
        {users.map((u) => { const userSites = allSites.filter((s) => s.userId === u.id); return (
          <div className="user-row" key={u.id}>
            <div className="user-main">
              <div className="user-id-line"><span className="user-email">{u.email}</span><span className={`role-tag ${u.role}`} title={u.role === 'admin' ? 'Полный доступ: пользователи, ключи, все сайты' : u.role === 'viewer' ? 'Только просмотр своих сайтов и отчётов' : 'Управление своими сайтами и ключами'}>{u.role}</span><span className={`perm-pill ${u.role}`}><i className="perm-dot" />{u.role === 'admin' ? 'полный доступ' : u.role === 'viewer' ? 'только просмотр' : 'свои сайты'}</span><span className="sites-count">{u.sitesCount} сайт(ов)</span></div>
              <div className="user-sub">{u.name || '—'} · chat: {u.telegramChatId || 'не задан'}</div>
            </div>
            <div className="user-actions">
              {editingId === u.id ? <><button className="ghost-btn" onClick={() => saveEdit(u.id)}>💾 Сохранить</button><button className="ghost-btn" onClick={() => setEditingId(null)}>Отмена</button></>
                : <><button className="ghost-btn tiny" onClick={() => setOpen({ ...open, [u.id]: !open[u.id] })}>📋 Сайты ({userSites.length})</button><button className="ghost-btn" onClick={() => startEdit(u)}>⚙️ Настроить</button><button className="ghost-btn danger" onClick={() => remove(u.id)}>🗑️</button></>}
            </div>
            {open[u.id] && (
              <div className="user-sites">
                {userSites.length === 0 ? <span className="user-sites-empty">У пользователя пока нет сайтов.</span> : userSites.map((s) => (
                  <div className="user-site-row" key={s.id}>
                    <span className="mono">{s.domain}</span>
                    <span className="user-site-name">{s.name && s.name !== s.domain ? s.name : ''}</span>
                    <span className={`badge ${s.uptime_data?.online ? 'green' : 'red'}`}>{s.uptime_data?.online ? 'Online' : 'Offline'}</span>
                  </div>
                ))}
              </div>
            )}
            {editingId === u.id && (
              <div className="user-edit">
                <label>Имя<input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></label>
                <label>Роль<select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}><option value="user">user</option><option value="admin">admin</option><option value="viewer">viewer</option></select></label>
                <label>Telegram chat ID<input type="text" value={edit.telegramChatId} onChange={(e) => setEdit({ ...edit, telegramChatId: e.target.value })} /></label>
                <label>Новый пароль <span className="opt">(необязательно)</span><input type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} placeholder="оставьте пустым" /></label>
              </div>
            )}
          </div>
        ); })}
      </div>
    </div>
  );
};

const TABS = [
  { id: 'sites', label: 'Сайты', icon: '📊' },
  { id: 'keys', label: 'API-ключи', icon: '🔑' }
];

function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [tab, setTab] = useState(() => localStorage.getItem('lastTab') || 'sites');
  const changeTab = (id) => { setTab(id); localStorage.setItem('lastTab', id); };

  useEffect(() => {
    const onLogout = () => setUser(null);
    window.addEventListener('auth:logout', onLogout);
    if (localStorage.getItem('token')) {
      api.get('/auth/me').then(({ data }) => { setUser(data); localStorage.setItem('user', JSON.stringify(data)); }).catch(() => {});
    }
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  const logout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user'); localStorage.removeItem('lastTab'); setUser(null);
  };

  if (!user) return <Login onLogin={setUser} />;

  const tabs = user.role === 'admin' ? [...TABS, { id: 'users', label: 'Пользователи', icon: '👥' }] : (user.role === 'viewer' ? [TABS[0]] : TABS);
  const effectiveTab = user.role === 'viewer' ? 'sites' : tab;

  return (
    <div className="dashboard">
      <header className="app-header">
        <div className="header-brand"><span className="live-dot" /> MONITOR</div>
        <nav className="header-tabs">
          {tabs.map((t) => (
            <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => changeTab(t.id)}>
              <span className="tab-icon">{t.icon}</span>{t.label}
            </button>
          ))}
        </nav>
        <div className="header-user">
          <div className="user-chip">
            <span className="user-chip-name">{user.name || user.email}</span>
            <span className={`role-tag ${user.role}`}>{user.role}</span>
          </div>
          <button className="logout-btn" onClick={logout} title="Выйти" aria-label="Выйти">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>
      <main className="app-main">
        {effectiveTab === 'sites' && <SitesView user={user} />}
        {effectiveTab === 'keys' && <ApiKeysView user={user} />}
        {tab === 'users' && user.role === 'admin' && <UsersView />}
      </main>
    </div>
  );
}

export default App;